/**
 * GT-FLEET-OWNER-SMOKE-STRUCTURAL-RECOVERY-01 — one CSV dialect
 * contract, shared by the client preview validator (`apps/web`) and the
 * server importer (`services/api`).
 *
 * ## The defect
 *
 * The Owner downloaded an import template and opened it in German
 * desktop Excel. The comma-separated header row and the comma-separated
 * example row each landed in a SINGLE cell.
 *
 * That is a delimiter failure, not an encoding one. Excel parses a
 * `.csv` using the system LIST SEPARATOR, which is `;` on German and
 * French Windows and `,` on English. A UTF-8 BOM fixes umlauts and
 * Arabic; it does nothing about the delimiter.
 *
 * ## The contract
 *
 * Download side (`toSpreadsheetCsv`): UTF-8 BOM, a leading `sep=,`
 * directive, comma-delimited data, CRLF line endings. The directive is
 * honoured by Excel in EVERY locale, so one file opens into real
 * columns for DE, EN, FR and AR without locale detection.
 *
 * Upload side (`detectCsvDialect`): German Excel SAVES csv back with
 * `;` and drops the directive, so the return leg of the roundtrip must
 * accept semicolons too. Detection is deterministic, never
 * probabilistic:
 *
 *   1. strip a UTF-8 BOM;
 *   2. if a leading `sep=<char>` directive is present, use it and drop
 *      that line;
 *   3. otherwise split the HEADER LINE ONLY on `,` and on `;` and ask
 *      the caller's schema which one produces a valid header;
 *   4. exactly one valid match wins;
 *   5. zero or two valid matches raise `AmbiguousCsvDialectError`.
 *
 * Rows are never sniffed individually — the dialect is decided once,
 * from the header, and applied to the whole file. A file that cannot be
 * decided fails loudly instead of being half-parsed.
 */

export const CSV_BOM = "﻿";

/** The delimiters this product accepts. Closed on purpose. */
export const CSV_DELIMITERS = [",", ";"] as const;
export type CsvDelimiter = (typeof CSV_DELIMITERS)[number];

/** Emitted as the first line of every downloaded template. */
export const CSV_SEPARATOR_DIRECTIVE = "sep=,";

export class AmbiguousCsvDialectError extends Error {
  readonly code = "ambiguous_csv_dialect" as const;
  /** The delimiters that produced a valid header — 0 or 2 of them. */
  readonly candidates: readonly CsvDelimiter[];
  constructor(candidates: readonly CsvDelimiter[]) {
    super(
      candidates.length === 0
        ? "the header row matched no supported delimiter"
        : "the header row is valid under more than one delimiter",
    );
    this.name = "AmbiguousCsvDialectError";
    this.candidates = candidates;
  }
}

/** Remove a leading UTF-8 BOM, which Excel writes and parsers choke on. */
export function stripCsvBom(text: string): string {
  return text.startsWith(CSV_BOM) ? text.slice(CSV_BOM.length) : text;
}

/**
 * Read a leading `sep=<char>` directive.
 *
 * Excel writes and honours this line; other tools see it as a junk
 * first row, which is exactly why it must be consumed here rather than
 * handed to the row parser.
 */
export function readSeparatorDirective(
  text: string,
): { readonly delimiter: CsvDelimiter; readonly rest: string } | null {
  const newline = text.search(/\r?\n/);
  const firstLine = (newline === -1 ? text : text.slice(0, newline)).trim();
  const match = /^sep=(.)$/i.exec(firstLine);
  if (!match) return null;
  const char = match[1] as CsvDelimiter;
  if (!CSV_DELIMITERS.includes(char)) return null;
  const rest = newline === -1 ? "" : text.slice(newline).replace(/^\r?\n/, "");
  return { delimiter: char, rest };
}

export interface CsvDialect {
  readonly delimiter: CsvDelimiter;
  /** The document with BOM and any `sep=` directive removed. */
  readonly body: string;
  /** How the delimiter was decided — useful in diagnostics. */
  readonly source: "directive" | "header";
}

/**
 * Decide the dialect of a CSV document.
 *
 * `isValidHeader` is supplied by the caller because only the caller
 * knows its own column contract; keeping it out of here means this
 * module never has to learn about vehicles, drivers or devices.
 */
export function detectCsvDialect(
  text: string,
  isValidHeader: (columns: readonly string[]) => boolean,
): CsvDialect {
  const withoutBom = stripCsvBom(text);

  const directive = readSeparatorDirective(withoutBom);
  if (directive) {
    return { delimiter: directive.delimiter, body: directive.rest, source: "directive" };
  }

  const newline = withoutBom.search(/\r?\n/);
  const headerLine = newline === -1 ? withoutBom : withoutBom.slice(0, newline);

  const viable = CSV_DELIMITERS.filter((delimiter) =>
    isValidHeader(splitCsvLine(headerLine, delimiter)),
  );
  if (viable.length !== 1) throw new AmbiguousCsvDialectError(viable);

  return { delimiter: viable[0]!, body: withoutBom, source: "header" };
}

/**
 * Split ONE csv line, honouring double-quoted fields.
 *
 * Deliberately minimal: it exists so the header can be inspected before
 * a delimiter is chosen. Full row parsing stays with each consumer's
 * existing parser, which is then told which delimiter to use.
 */
export function splitCsvLine(line: string, delimiter: CsvDelimiter): string[] {
  const out: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (line[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(field);
      field = "";
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out.map((f) => f.trim());
}

/**
 * Wrap canonical comma/LF csv content as Excel-friendly download bytes.
 *
 * BOM so non-ASCII survives (German umlauts, Arabic); `sep=,` so every
 * Excel locale opens it into real columns; CRLF because that is what
 * desktop spreadsheets write and expect.
 */
export function toSpreadsheetCsv(csv: string): string {
  const body = csv.replace(/\r\n/g, "\n").replace(/\n/g, "\r\n");
  return `${CSV_BOM}${CSV_SEPARATOR_DIRECTIVE}\r\n${body}`;
}
